import AutoComplete, {
  AutoCompleteHighLight,
  AutoCompleteSuggestion,
} from '@eversports/react-components/primitives/AutoComplete'
import { PyroFormValues } from 'pyro-form'
import { ReactElement, useEffect, useMemo, useState } from 'react'

import { useIntl } from '../localization/react'
import { AddressProps } from '../registration-context'

interface AddressSuggestion {
  id: string
  description: string
  country: string
}

enum AddressType {
  'route' = 'street',
  'street_number' = 'streetNumber',
  'locality' = 'city',
  'postal_code' = 'zip',
  'country' = 'country',
}

export interface AutoCompleteAddressProps<Values extends PyroFormValues> {
  name: Extract<keyof Values, string>
  onChange: (values: AddressProps) => void
  searchValue?: string
  label?: string | ReactElement<any>
  className?: string
}

interface Coord {
  latitude?: number
  longitude?: number
  accuracy?: number
  // eslint-disable-next-line no-undef
  location?: google.maps.LatLng
}

const addressValuesInitialState = {
  street: '',
  streetNumber: '',
  city: '',
  zip: '',
  country: '',
}

const AutoCompleteAddress = <Values extends PyroFormValues>({
  onChange,
  searchValue,
  name,
  label,
  className,
}: AutoCompleteAddressProps<Values>) => {
  const intl = useIntl()
  const [addressSuggestions, setAddressSuggestions] = useState<Array<AddressSuggestion>>([])
  const [coords, setCoords] = useState<Coord>()
  const [search, setSearch] = useState(searchValue)
  const [googleMapsReady, setGoogleMapsReady] = useState(typeof window !== 'undefined' && !!window.google?.maps?.places)

  // Track Google Maps API availability
  useEffect(() => {
    const checkGoogleMaps = () => {
      const isReady = typeof window !== 'undefined' && !!window.google?.maps?.places
      setGoogleMapsReady(isReady)
    }

    // Check immediately
    checkGoogleMaps()

    // Check periodically in case API loads/reloads
    const interval = setInterval(checkGoogleMaps, 1000)

    return () => clearInterval(interval)
  }, [])

  const autoCompleteService = useMemo(() => {
    if (typeof window !== 'undefined' && window.google?.maps?.places) {
      // eslint-disable-next-line no-undef
      return new google.maps.places.AutocompleteService()
    }
    return null
  }, [googleMapsReady])

  const geoCoder = useMemo(() => {
    if (typeof window !== 'undefined' && window.google?.maps) {
      // eslint-disable-next-line no-undef
      return new google.maps.Geocoder()
    }
    return null
  }, [googleMapsReady])

  useEffect(() => {
    getGeoLocation()
  }, [coords])

  useEffect(() => {
    if (searchValue !== search) {
      onChange(addressValuesInitialState)
      getPlaceAutocomplete()
      setSearch(searchValue)
    }
  }, [searchValue])

  const getPlaceAutocomplete = () => {
    if (!searchValue || !autoCompleteService) {
      return
    }

    // eslint-disable-next-line no-undef
    const config: google.maps.places.AutocompletionRequest = {
      input: searchValue,
      types: ['address'],
    }

    if (coords && coords.location) {
      config.location = coords.location
      config.radius = coords.accuracy
    }

    autoCompleteService.getPlacePredictions(config, (predictions) => {
      const suggestions =
        predictions &&
        predictions.map((prediction) => ({
          id: prediction.place_id,
          description: prediction.description,
          country: prediction.terms[prediction.terms.length - 1].value,
        }))

      if (suggestions) setAddressSuggestions(suggestions)
    })
  }

  const getGeoLocation = () => {
    if (navigator.geolocation && !coords) {
      navigator.geolocation.getCurrentPosition(({ coords: { longitude, latitude, accuracy } }) => {
        const location =
          // eslint-disable-next-line no-undef
          typeof window !== 'undefined' ? window.google && new google.maps.LatLng(latitude, longitude) : undefined
        setCoords({ latitude, longitude, accuracy, location })
      })
    }
  }

  const getPlaceDetails = (placeId: string) => {
    if (!geoCoder) {
      return
    }

    geoCoder.geocode({ placeId }, (result, status) => {
      if (String(status) === 'OK' && result) {
        const addressValues: AddressProps = {
          ...addressValuesInitialState,
          longitude: result[0].geometry.location.lng(),
          latitude: result[0].geometry.location.lat(),
        }
        for (const addressComponent of result[0].address_components) {
          for (const type of addressComponent.types) {
            const addressType = AddressType[type as keyof typeof AddressType]
            if (addressType) {
              addressValues[addressType] =
                addressType === AddressType.country ? addressComponent.short_name : addressComponent.long_name
            }
          }
        }
        onChange(addressValues)
      }
    })
  }

  return (
    <AutoComplete
      name={name}
      label={label}
      suggestions={addressSuggestions}
      defaultValue={searchValue}
      filterBy="description"
      placeholder={intl.startTypingForGoogleSearch()}
      className={className}
    >
      {({ suggestions, onClick, query }) =>
        suggestions.map((suggestion) => (
          <AutoCompleteSuggestion
            key={suggestion.id}
            onClick={() => {
              onClick(suggestion.id)
              getPlaceDetails(suggestion.id)
            }}
          >
            <AutoCompleteHighLight suggestion={suggestion.description} query={query} />
          </AutoCompleteSuggestion>
        ))
      }
    </AutoComplete>
  )
}

export default AutoCompleteAddress
